import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { formatCurrency } from '@/lib/formatters'
import { lazy,Suspense } from 'react'
import { api,type AxiosError } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { z } from 'zod'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card,CardContent,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
X,
Plus,
MoreHorizontal,
MoreVertical,
Pencil,
Trash2,
Loader2,
Search,
Scale,Check,
Clock,
RefreshCw
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { queryKeys } from '@/lib/query-keys'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { PricingScheme,TieringMethod } from '@/types'
import { formatInStoreTz } from '@/lib/time'

const AddEditPricingDialog = lazy(() =>
  import('@/features/catalog/dialogs/add-edit-pricing-dialog').then(m => ({
    default: m.AddEditPricingDialog,
  }))
)

interface UomItem {
  id: string
  name: string
  shortForm: string
  measurementType: string
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page?: number
    total?: number
    totalPages?: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/pricing')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  component: PricingLayout,
  validateSearch: searchSchema,
})

let suppressedPricingAutoOpenSignature: string | null = null

function PricingLayout() {
  return <Outlet />
}

export function PricingPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const pageSignature = `${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()

  const [deleteId, setDeleteId] = React.useState<string | null>(null)
  const [deleteTierId, setDeleteTierId] = React.useState<string | null>(null)

  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [roundingRule, setRoundingRule] = React.useState('USE_PREVIOUS_TIER')

  const [isSaving, setIsSaving] = React.useState(false)

  // Form State
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [label, setLabel] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [tieringMethod, setTieringMethod] = React.useState<TieringMethod>(
    TieringMethod.WEIGHT_BASED
  )
  const [weightUomId, setWeightUomId] = React.useState('')
  const [toleranceValue, setToleranceValue] = React.useState('0.2')

  // Tier Form State for Modal
  const [defaultTiers, setDefaultTiers] = React.useState<
    { start: string; price: string }[]
  >([])

  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)

  // Accordion state
  const [accordionValue, setAccordionValue] = React.useState<string>('')

  // Details Panel State
  const [isAddingTierMode, setIsAddingTierMode] = React.useState(false)
  const [newTierStart, setNewTierStart] = React.useState('')
  const [newTierPrice, setNewTierPrice] = React.useState('')
  const [tierPanelErrors, setTierPanelErrors] = React.useState<{
    start?: string
    price?: string
  }>({})

  // Validation State
  const [validationErrors, setValidationErrors] = React.useState<{
    label?: string
    weightUom?: string
  }>({})
  const [tierErrors, setTierErrors] = React.useState<Record<number, string>>({})

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/pricing/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

      navigate({ to: '/catalog/pricing', search: (prev) => ({ ...prev }) })
    },
    [navigate],
  )

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const schemesQuery = useQuery({
    queryKey: [...queryKeys.pricingTiers('', { page, limit, search: searchQuery })],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = { page, limit, includeSystemManaged: false }
      if (searchQuery) params.search = searchQuery

      const response = await api.get<{ data?: PricingScheme[]; meta?: { total?: number; totalPages?: number } }>(`/pricing-schemes`, { params })
      const schemeData = Array.isArray(response.data) ? response.data : []
      const meta = response.meta || { total: schemeData.length, totalPages: 1 }
      return { data: schemeData as PricingScheme[], meta }
    },
  })

  const schemes = schemesQuery.data?.data || []
  const totalItems = schemesQuery.data?.meta?.total || schemes.length
  const totalPages = schemesQuery.data?.meta?.totalPages || 1
  const loading = schemesQuery.isLoading
  const currentPageHasSelectedScheme = !!selectedId && schemes.some((scheme) => scheme.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedScheme) return selectedId
    if (loading || schemes.length === 0) return undefined
    if (!selectedId && suppressedPricingAutoOpenSignature === pageSignature) return undefined
    return schemes[0].id
  })()

  const weightUomsQuery = useQuery({
    queryKey: queryKeys.weightUoms(),
    queryFn: async () => {
      const response = await api.get<{ data?: UomItem[] }>(`/units-of-measurement?measurementType=WEIGHT`)
      return (response.data || []) as UomItem[]
    },
  })
  const weightUoms = React.useMemo(() => weightUomsQuery.data || [], [weightUomsQuery.data])

  const productCountsQuery = useQuery({
    queryKey: queryKeys.pricingProductCounts(),
    queryFn: async () => {
      const response = await api.get<{ data?: { pricingSchemeId?: string; pricingScheme?: { id: string } }[] }>(`/sale-configs`)
      const configs = response.data || []
      const counts: Record<string, number> = {}
      configs.forEach((cfg: { pricingSchemeId?: string; pricingScheme?: { id: string } }) => {
        const schemeId = cfg.pricingSchemeId || cfg.pricingScheme?.id
        if (schemeId) {
          counts[schemeId] = (counts[schemeId] || 0) + 1
        }
      })
      return counts
    },
  })
  const productCounts = productCountsQuery.data || {}

  const selectedScheme = schemes.find((s) => s.id === effectiveSelectedId)

  const closeSelectedScheme = React.useCallback(() => {
    suppressedPricingAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/pricing')) {
          suppressedPricingAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const toggleStatusMutation = useMutation<void, unknown, PricingScheme, OptimisticPaginatedContext<PricingScheme>>({
    mutationFn: async (scheme: PricingScheme) => {
      await api.patch(`/pricing-schemes/${scheme.id}`, { isActive: !scheme.isActive })
    },
    onMutate: async (scheme) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.pricingTiers('') })
      const previousData = queryClient.getQueriesData<PaginatedData<PricingScheme>>({ queryKey: queryKeys.pricingTiers('') })

      queryClient.setQueriesData<PaginatedData<PricingScheme>>(
        { queryKey: queryKeys.pricingTiers('') },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map((item) =>
              item.id === scheme.id ? { ...item, isActive: !item.isActive } : item
            ),
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_data, scheme) => {
      toast.success('Success', { description: `Scheme ${!scheme.isActive ? 'activated' : 'deactivated'}` })
    },
    onError: (_error, _scheme, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Error', { description: 'Failed to update status' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
    },
  })

  const deleteSchemeMutation = useMutation<void, unknown, string, OptimisticPaginatedContext<PricingScheme>>({
    mutationFn: async (id: string) => {
      await api.delete(`/pricing-schemes/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.pricingTiers('') })
      const previousData = queryClient.getQueriesData<PaginatedData<PricingScheme>>({ queryKey: queryKeys.pricingTiers('') })

      queryClient.setQueriesData<PaginatedData<PricingScheme>>(
        { queryKey: queryKeys.pricingTiers('') },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, total: Math.max(0, (old.meta.total || 0) - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_data, id) => {
      if (selectedId === id) setSelectedId(null)
      toast.success('Success', { description: 'Scheme permanently deleted' })
    },
    onError: (_error, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Error', { description: 'Failed to delete scheme' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
      setDeleteId(null)
    },
  })

  const addTierMutation = useMutation({
    mutationFn: async (payload: { startQuantity: string; unitPriceCents: number }) => {
      await api.post(`/pricing-schemes/${selectedId}/tiers`, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
      setNewTierStart('')
      setNewTierPrice('')
      setTierPanelErrors({})
      setIsAddingTierMode(false)
      toast.success('Success', { description: 'Tier added' })
    },
    onError: (error: unknown) => {
      const msg = (error as AxiosError<{ message: string }>)?.response?.data?.message || 'Failed to add tier'
      const lowerMsg = msg.toLowerCase()
      const newErrors: { start?: string; price?: string } = {}
      if (lowerMsg.includes('exist') || lowerMsg.includes('duplicate')) {
        newErrors.start = 'Tier start value already exists'
      } else if (lowerMsg.includes('start')) {
        newErrors.start = msg
      } else if (lowerMsg.includes('price')) {
        newErrors.price = msg
      }
      if (Object.keys(newErrors).length > 0) {
        setTierPanelErrors(newErrors)
        toast.error('Validation Error', { description: 'Please check the highlighted fields' })
      } else {
        toast.error('Error', { description: msg })
      }
    },
  })

  const deleteTierMutation = useMutation({
    mutationFn: async (tierId: string) => {
      await api.delete(`/pricing-tiers/${tierId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
      toast.success('Success', { description: 'Tier deleted' })
    },
    onError: () => {
      toast.error('Error', { description: 'Failed to delete tier' })
    },
    onSettled: () => {
      setDeleteTierId(null)
    },
  })

  // Auto-select first weight unit when adding new scheme
  React.useEffect(() => {
    if (
      isAddDialogOpen &&
      !editingId &&
      tieringMethod === TieringMethod.WEIGHT_BASED &&
      !weightUomId &&
      weightUoms.length > 0
    ) {
      setWeightUomId(weightUoms[0].id)
    }
  }, [isAddDialogOpen, editingId, tieringMethod, weightUomId, weightUoms])

  // Auto-add first tier start=1 when switching to Quantity method (if empty)
  React.useEffect(() => {
    if (
      isAddDialogOpen &&
      !editingId &&
      tieringMethod === TieringMethod.QUANTITY_BASED &&
      defaultTiers.length === 0
    ) {
      setDefaultTiers([{ start: '1', price: '' }])
    }
  }, [isAddDialogOpen, editingId, tieringMethod, defaultTiers])

  // Auto-populate tiers logic — only for new schemes, not when editing
  React.useEffect(() => {
    if (tieringMethod === TieringMethod.WEIGHT_BASED && isAddDialogOpen && !editingId) {
      // Functional update: no dependency on the current tiers value.
      setDefaultTiers((prev) => prev.length === 0
        ? [
            { start: '1', price: '' },
            { start: '3.5', price: '' },
            { start: '7', price: '' },
            { start: '14', price: '' },
            { start: '28', price: '' },
            { start: '29', price: '' },
          ]
        : prev)
    } else if (tieringMethod !== TieringMethod.WEIGHT_BASED && !editingId) {
      // Functional update: no dependency on the current tiers value.
      setDefaultTiers((prev) => (prev.length > 1 ? [] : prev))
    }
  }, [tieringMethod, isAddDialogOpen, editingId])

  const handleEditScheme = (scheme: PricingScheme) => {
    setEditingId(scheme.id)
    setLabel(scheme.label)
    setDescription(scheme.description || '')
    setTieringMethod(
      (scheme.tieringMethod || TieringMethod.WEIGHT_BASED) as TieringMethod
    )
    setWeightUomId(scheme.weightUomId || '')
    setToleranceValue(
      scheme.toleranceValue ? String(scheme.toleranceValue) : '0.2'
    )
    setRoundingRule(scheme.roundingRule || 'USE_PREVIOUS_TIER')

    if (scheme.toleranceValue || scheme.roundingRule) {
      setAccordionValue('advanced')
    } else {
      setAccordionValue('')
    }

    if (scheme.tiers && scheme.tiers.length > 0) {
      const mappedTiers = scheme.tiers
        .map((t) => ({
          start: String(t.startQuantity),
          price: (t.unitPriceCents / 100).toFixed(2),
        }))
        .sort((a, b) => Number(a.start) - Number(b.start))
      setDefaultTiers(mappedTiers)
    } else {
      setDefaultTiers([])
    }

    setIsAddDialogOpen(true)
  }

  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setEditingId(null)
        setLabel('')
        setDescription('')
        setTieringMethod(TieringMethod.WEIGHT_BASED)
        setWeightUomId('')
        setToleranceValue('0.2')
        setRoundingRule('USE_PREVIOUS_TIER')
        setDefaultTiers([])
        setAccordionValue('')
      }, 300)
    }
  }

  const handleSaveScheme = async () => {
    setValidationErrors({})
    setTierErrors({})

    let hasErrors = false
    const newErrors: { label?: string; weightUom?: string } = {}

    if (label.trim().length === 0) {
      newErrors.label = 'Label is required'
      hasErrors = true
    }
    if (
      tieringMethod === TieringMethod.WEIGHT_BASED &&
      weightUomId.length === 0
    ) {
      newErrors.weightUom = 'Weight unit is required'
      hasErrors = true
    }

    if (hasErrors) {
      setValidationErrors(newErrors)
      return
    }

    try {
      setIsSaving(true)

      const payload = {
        label,
        description,
        tieringMethod,
        weightUomId:
          tieringMethod === TieringMethod.WEIGHT_BASED ? weightUomId : undefined,
        toleranceValue:
          tieringMethod === TieringMethod.WEIGHT_BASED
            ? toleranceValue
            : undefined,
        roundingRule: roundingRule || undefined,
      }

      let savedSchemeId = editingId

      if (editingId) {
        await api.patch(`/pricing-schemes/${editingId}`,
          payload
        )
      } else {
        const response = await api.post<{ id?: string; data?: { id?: string } }>(`/pricing-schemes`,
          { ...payload, isActive: true }
        )
        savedSchemeId = response.id || response.data?.id || null
        if (savedSchemeId) setEditingId(savedSchemeId)
      }

      if (!savedSchemeId && !editingId)
        throw new Error('Failed to get Scheme ID')

      if (savedSchemeId) {
        if (editingId) {
          const originalScheme = schemes.find((s) => s.id === editingId)
          if (originalScheme?.tiers) {
            await Promise.all(
              originalScheme.tiers.map((t) =>
                api
                  .delete(
                    `/pricing-tiers/${t.id}`
                  )
                  .catch((_e: unknown) => {})
              )
            )
          }
        }

        const tiersToProcess = defaultTiers
          .map((tier, index) => ({ ...tier, originalIndex: index }))
          .filter(
            (t) =>
              (t.start && t.start.trim() !== '') ||
              (t.price && t.price.trim() !== '')
          )

        let tierFailures = 0
        for (const tier of tiersToProcess) {
          try {
            if (!tier.start)
              throw {
                response: { data: { message: 'Start quantity is required' } },
              }
            if (!tier.price)
              throw { response: { data: { message: 'Price is required' } } }

            const start = parseFloat(tier.start)
            const price = parseFloat(tier.price)

            if (start < 0)
              throw {
                response: {
                  data: { message: 'Start quantity must be positive' },
                },
              }

            await api.post(`/pricing-schemes/${savedSchemeId}/tiers`,
              {
                startQuantity: tier.start,
                unitPriceCents: Math.round(price * 100),
              }
            )
          } catch (e: unknown) {
            setTierErrors((prev) => ({
              ...prev,
              [tier.originalIndex]:
                (e as AxiosError<{ message: string }>)?.response?.data
                  ?.message || 'Failed to save tier',
            }))
            tierFailures++
          }
        }

        if (tierFailures > 0) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
          toast.error('Warning', {
            description: `${tierFailures} tiers failed to save. Please check errors.`,
          })
          setIsSaving(false)
          return
        }
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.pricingTiers('') })
      handleCloseDialog(false)
      toast.success('Success', {
        description: `Pricing Scheme ${
          editingId ? 'updated' : 'created'
        } successfully`,
      })
    } catch (error: unknown) {
      toast.error('Error', {
        description:
          (error as AxiosError<{ message: string }>)?.response?.data?.message ||
          'Failed to save scheme',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleStatus = (scheme: PricingScheme) => toggleStatusMutation.mutate(scheme)

  const handleDetailsAddTier = async () => {
    if (!selectedId) return
    setTierPanelErrors({})
    const errors: { start?: string; price?: string } = {}
    let hasError = false
    if (!newTierStart) { errors.start = 'Required'; hasError = true }
    if (!newTierPrice) { errors.price = 'Required'; hasError = true }
    const start = parseFloat(newTierStart)
    const price = parseFloat(newTierPrice)
    if (isNaN(start) || start < 0) { errors.start = 'Must be a positive number'; hasError = true }
    if (isNaN(price) || price < 0) { errors.price = 'Must be a positive number'; hasError = true }
    if (hasError) { setTierPanelErrors(errors); return }

    addTierMutation.mutate({
      startQuantity: newTierStart,
      unitPriceCents: Math.round(price * 100),
    })
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Pricing Tiers</h2>
          <p className="text-muted-foreground">
            Manage tiered pricing schemes for your products.
          </p>
        </div>
        <div className="flex items-center gap-2"></div>
        <Button
          onClick={() => setIsAddDialogOpen(true)}
          className="transition-all hover:scale-105"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Tiered Pricing
        </Button>
        <Suspense fallback={null}>
          {isAddDialogOpen && (
            <AddEditPricingDialog
              open={isAddDialogOpen}
              onClose={() => handleCloseDialog(false)}
              onSave={handleSaveScheme}
              isPending={isSaving}
              isEditing={!!editingId}
              label={label}
              onLabelChange={setLabel}
              description={description}
              onDescriptionChange={setDescription}
              tieringMethod={tieringMethod}
              onTieringMethodChange={setTieringMethod}
              weightUomId={weightUomId}
              onWeightUomIdChange={setWeightUomId}
              weightUoms={weightUoms}
              tiers={defaultTiers}
              onTiersChange={setDefaultTiers}
              toleranceValue={toleranceValue}
              onToleranceChange={setToleranceValue}
              roundingRule={roundingRule}
              onRoundingRuleChange={setRoundingRule}
              accordionValue={accordionValue}
              onAccordionChange={setAccordionValue}
              validationErrors={validationErrors}
              onClearValidationError={(field) => {
                setValidationErrors({ ...validationErrors, [field]: undefined })
              }}
              tierErrors={tierErrors}
              onClearTierError={(index) => {
                const newErrors = { ...tierErrors }
                delete newErrors[index]
                setTierErrors(newErrors)
              }}
            />
          )}
        </Suspense>
      </div>

      <div className="flex items-center gap-2 max-w-sm w-full">
        <div className="relative flex-1 group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search pricing schemes..."
            className="pl-9 pr-8"
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchInput('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          title="Refresh"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true)
            void schemesQuery.refetch()
            setTimeout(() => setIsRefreshing(false), 600)
          }}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ${
            !loading && schemes.length === 0 ? '' : 'rounded-md squircle border overflow-hidden'
          } ${
            selectedId
              ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]'
              : 'w-full'
          }`}
        >
          {!loading && schemes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img
                  src="/misc/logo.png"
                  alt="Rootpoint Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">
                  No pricing schemes found
                </p>
                <Button
                  onClick={() => {
                    setEditingId(null)
                    setIsAddDialogOpen(true)
                  }}
                  className="w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Pricing Scheme
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-[40%]">Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[80px] text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell className="w-[40%]">
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-[160px]" />
                          <Skeleton className="h-3 w-[120px] opacity-70" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Skeleton className="h-5 w-[80px] rounded-full" />
                          <Skeleton className="h-5 w-[60px] rounded-full" />
                          <Skeleton className="h-5 w-[70px] rounded-full" />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-8 w-8 ml-auto rounded-md" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  schemes.map((scheme) => (
                    <TableRow
                      key={scheme.id}
                      className={`cursor-pointer transition-colors group ${
                        effectiveSelectedId === scheme.id ? 'bg-muted' : ''
                      } ${!scheme.isActive ? 'opacity-60 bg-muted/20' : ''}`}
                      onClick={() => {
                        if (effectiveSelectedId === scheme.id) {
                          closeSelectedScheme()
                          return
                        }
                        setSelectedId(scheme.id)
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className={`font-semibold ${
                              !scheme.isActive
                                ? 'line-through text-muted-foreground'
                                : ''
                            }`}
                          >
                            {scheme.label}
                          </div>
                          {!scheme.isActive && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] h-5 px-1.5 uppercase bg-muted text-muted-foreground"
                            >
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {scheme.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant="secondary"
                            className="text-[10px] font-normal"
                          >
                            {scheme.tieringMethod
                              ?.replace(/_/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase()) ||
                              scheme.pricingMethod
                                ?.replace(/_/g, ' ')
                                .replace(/\b\w/g, (c) => c.toUpperCase()) ||
                              'N/A'}
                          </Badge>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="text-[10px] font-normal cursor-help"
                                >
                                  {scheme.tiers?.length || 0} tiers
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="p-2">
                                {scheme.tiers && scheme.tiers.length > 0 ? (
                                  <div className="grid gap-1">
                                    <p className="font-semibold text-xs mb-1">
                                      Pricing Breakpoints
                                    </p>
                                    {[...scheme.tiers]
                                      .sort(
                                        (a, b) =>
                                          Number(a.startQuantity) -
                                          Number(b.startQuantity)
                                      )
                                      .map((tier, idx) => {
                                        const unitLabel =
                                          scheme.tieringMethod ===
                                          TieringMethod.QUANTITY_BASED
                                            ? 'unit'
                                            : scheme.weightUom?.shortForm || ''
                                        return (
                                          <div
                                            key={idx}
                                            className="text-[10px] flex justify-between gap-4"
                                          >
                                            <span>
                                              Starts at {tier.startQuantity}{' '}
                                              {unitLabel}:
                                            </span>
                                            <span className="font-mono">
                                              {formatCurrency(
                                                tier.unitPriceCents
                                              )}
                                              /{unitLabel}
                                            </span>
                                          </div>
                                        )
                                      })}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    No tiers configured
                                  </span>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant={
                                    productCounts[scheme.id]
                                      ? 'default'
                                      : 'secondary'
                                  }
                                  className={`text-[10px] font-normal cursor-help ${
                                    !productCounts[scheme.id]
                                      ? 'text-muted-foreground bg-muted'
                                      : ''
                                  }`}
                                >
                                  {productCounts[scheme.id] || 0} products
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Used by {productCounts[scheme.id] || 0}{' '}
                                  products
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"
                            >
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem
                              className="gap-1.5"
                              onClick={(e: { stopPropagation: () => void }) => {
                                e.stopPropagation()
                                handleEditScheme(scheme)
                              }}
                            >
                              <Pencil className="h-4 w-4" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="gap-1.5"
                              onClick={(e: { stopPropagation: () => void }) => {
                                e.stopPropagation()
                                handleToggleStatus(scheme)
                              }}
                            >
                              {scheme.isActive ? (
                                <X className="h-4 w-4" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                              {scheme.isActive ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="gap-1.5 text-destructive focus:text-destructive"
                              onClick={(e: { stopPropagation: () => void }) => {
                                e.stopPropagation()
                                setDeleteId(scheme.id)
                              }}
                            >
                              <Trash2 className="h-4 w-4" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          {(schemes.length > 0 || loading) && (
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={limit}
              onPageChange={(p) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
              onLimitChange={(l) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
              entityName="schemes"
              isLoading={loading}
            />
          )}
        </div>

        {selectedScheme && (
          <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] shadow-lg overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 pb-4">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
              <div className="grid gap-1">
                <CardTitle className="text-xl flex items-center gap-1.5">
                  {selectedScheme.label}
                  {!selectedScheme.isActive && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-5 px-1.5 uppercase bg-muted text-muted-foreground"
                    >
                      Inactive
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-1.5 pt-1">
                  <Badge
                    variant="secondary"
                    className="rounded-sm font-normal text-xs"
                  >
                    {selectedScheme.tieringMethod
                      ?.replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase()) ||
                      selectedScheme.pricingMethod
                        ?.replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase()) ||
                      'N/A'}
                  </Badge>
                  {selectedScheme.weightUom && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Scale className="h-3 w-3" />{' '}
                      {selectedScheme.weightUom.shortForm}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 -mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 transition-transform duration-200"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">More options</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem className="gap-1.5" onClick={() => handleEditScheme(selectedScheme)}>
                      <Pencil className="h-4 w-4" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-1.5" onClick={() => handleToggleStatus(selectedScheme)}>
                      {selectedScheme.isActive ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {selectedScheme.isActive ? 'Deactivate' : 'Activate'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="gap-1.5 text-destructive focus:text-destructive"
                      onClick={() => setDeleteId(selectedScheme.id)}
                    >
                      <Trash2 className="h-4 w-4" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={closeSelectedScheme}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="font-medium leading-none text-muted-foreground text-xs uppercase tracking-wider">
                    Description
                  </h4>
                  <p className="text-sm text-foreground">
                    {selectedScheme.description || 'No description provided.'}
                  </p>
                </div>

                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium leading-none text-muted-foreground text-xs uppercase tracking-wider">
                      Pricing Breakpoints
                    </h4>
                    {selectedScheme.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs gap-1 hover:text-primary"
                        onClick={() => setIsAddingTierMode(true)}
                      >
                        <Plus className="h-3 w-3" /> Add Tier
                      </Button>
                    )}
                  </div>

                  {isAddingTierMode && (
                    <div className="p-3 border rounded-md bg-muted/20 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">New Tier</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => setIsAddingTierMode(false)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <div>
                          <Label className="text-[10px] text-muted-foreground mb-1 block">
                            Start Quantity{' '}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="0"
                            className={`h-7 text-xs ${
                              tierPanelErrors.start ? 'border-destructive' : ''
                            }`}
                            value={newTierStart}
                            onChange={(e) => {
                              const val = e.target.value
                              if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                setNewTierStart(val)
                                if (tierPanelErrors.start)
                                  setTierPanelErrors((prev) => ({
                                    ...prev,
                                    start: undefined,
                                  }))
                              }
                            }}
                          />
                          {tierPanelErrors.start && (
                            <span className="text-[10px] text-destructive">
                              {tierPanelErrors.start}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="relative">
                        <Label className="text-[10px] text-muted-foreground mb-1 block">
                          Unit Price{' '}
                          <span className="text-destructive">*</span>
                        </Label>
                        <span className="absolute left-2 top-[26px] text-muted-foreground text-xs">
                          $
                        </span>
                        <Input
                          className={`h-7 pl-5 text-xs ${
                            tierPanelErrors.price ? 'border-destructive' : ''
                          }`}
                          placeholder="0.00"
                          value={newTierPrice}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setNewTierPrice(val)
                              if (tierPanelErrors.price)
                                setTierPanelErrors((prev) => ({
                                  ...prev,
                                  price: undefined,
                                }))
                            }
                          }}
                        />
                        {tierPanelErrors.price && (
                          <span className="text-[10px] text-destructive block mt-1">
                            {tierPanelErrors.price}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="w-full h-7 text-xs"
                        onClick={handleDetailsAddTier}
                        disabled={addTierMutation.isPending}
                      >
                        {addTierMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          'Save Tier'
                        )}
                      </Button>
                    </div>
                  )}

                  <div className="border rounded-md divide-y">
                    {selectedScheme.tiers && selectedScheme.tiers.length > 0 ? (
                      (() => {
                        const tiers = [...selectedScheme.tiers].sort(
                          (a, b) =>
                            Number(a.startQuantity) - Number(b.startQuantity)
                        )
                        const unitLabel =
                          selectedScheme.tieringMethod ===
                          TieringMethod.QUANTITY_BASED
                            ? 'unit'
                            : selectedScheme.weightUom?.shortForm || ''

                        return tiers.map((tier) => {
                          return (
                            <div
                              key={tier.id}
                              className="flex items-center justify-between p-2.5 text-sm hover:bg-muted/40 transition-colors group"
                            >
                              <div className="grid gap-0.5">
                                <div className="font-medium">
                                  Starts at {tier.startQuantity} {unitLabel}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-sm">
                                  {formatCurrency(tier.unitPriceCents)}/
                                  {unitLabel}
                                </span>
                                {selectedScheme.isActive && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                    onClick={() => setDeleteTierId(tier.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          )
                        })
                      })()
                    ) : (
                      <div className="p-8 text-center text-xs text-muted-foreground">
                        No tiers configured.
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Each price applies until the next breakpoint.
                  </p>
                </div>

                <div className="pt-2 mt-2 border-t grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium text-foreground block mb-1">
                      Created
                    </span>
                    {selectedScheme.createdAt
                      ? formatInStoreTz(selectedScheme.createdAt, 'MMM d, yyyy')
                      : 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground block mb-1">
                      Updated
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {selectedScheme.updatedAt
                        ? formatDistanceToNow(new Date(selectedScheme.updatedAt), {
                            addSuffix: true,
                          })
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onOpenChange={(open: boolean) => !open && setDeleteId(null)}
        title="Delete Pricing Scheme?"
        description={
          <>
            This action cannot be undone. This will permanently delete the pricing scheme{' '}
            <span className="font-medium text-foreground">
              "{schemes.find((s) => s.id === deleteId)?.label}"
            </span>{' '}
            and all its associated tiers.
          </>
        }
        onConfirm={() => {
          if (deleteId) deleteSchemeMutation.mutate(deleteId)
        }}
        confirmLabel="Delete Scheme"
        isPending={deleteSchemeMutation.isPending}
      />

      <DeleteConfirmationDialog
        open={!!deleteTierId}
        onOpenChange={(open: boolean) => !open && setDeleteTierId(null)}
        title="Delete Tier?"
        description="Are you sure you want to delete this pricing tier? This action cannot be undone and will immediately update all products using this scheme."
        onConfirm={() => {
          if (deleteTierId) deleteTierMutation.mutate(deleteTierId)
        }}
        confirmLabel="Delete Tier"
        isPending={deleteTierMutation.isPending}
      />
    </div>
  )
}
